import { useState, useEffect, useRef } from 'react';
import usePreferences, { PreferenceKey } from 'hooks/usePreferences';
import { setPrintListenerReady } from 'components/Notification/genericNotification';
import applicationNotification from 'screens/Applications&WorkOrders/Applications/Components/Notification';
import workOrderNotification from 'screens/Applications&WorkOrders/WorkOrders/Components/Notification';
import useOperationPdf, {
  ExportLanguageEnum,
  OrientationOptions
} from 'screens/Applications&WorkOrders/Hooks/useOperationPdf';
import useOperationProducts, {
  OperationType
} from 'screens/Applications&WorkOrders/Components/hooks/useOperationProducts';
import {
  FullProduct,
  Product
} from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import {
  getManufacturerName,
  getProductName
} from 'screens/ProductList/helpers/filteredProducts';
import { getTargetPests } from 'screens/Applications&WorkOrders/Components/helpers';
import { OperationType as OperationTypeEnum } from 'components/RecordsListView/constants';
import { OperationType as AgOperationType } from 'screens/Applications&WorkOrders/StepsLayout/Context/AgOperationUtilityProvider';
import useOperationEvents from 'hooks/useOperationEvents';
import { OperationDetails } from '../../types';

interface UsePrintSettingsModalProps {
  operation?: OperationDetails;
  userId: string;
  operationType: OperationType;
}

const usePrintSettingsModal = ({
  operation,
  userId,
  operationType
}: UsePrintSettingsModalProps) => {
  const routeAdminOperationType =
    operationType === 'work-order'
      ? OperationTypeEnum.WORK_ORDER
      : OperationTypeEnum.APPLICATION;
  const {
    sendPrintPdfEvent: sendPrintPdfAmplitudeEvent,
    sendDownloadPdfEvent
  } = useOperationEvents(routeAdminOperationType);
  const { findPreferenceByKey, upsertPreference } = usePreferences();
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [orientation, setOrientation] = useState<OrientationOptions>(
    'portrait'
  );
  const [printOperationId, setPrintOperationId] = useState<string | null>(null);
  const [pendingPrintOperation, setPendingPrintOperation] = useState<any>(null);
  const [language, setLanguage] = useState<ExportLanguageEnum>(
    ExportLanguageEnum.ENGLISH
  );
  const lastPdfActionRef = useRef<{
    language: ExportLanguageEnum;
    toPrint: boolean;
  } | null>(null);

  // Use the hook to fetch products for the application that needs to be printed
  const { products: printProducts } = useOperationProducts(
    printOperationId,
    operationType
  );

  const notification =
    operationType === 'work-order'
      ? workOrderNotification
      : applicationNotification;

  const { createPDF, downloadingPDF } = useOperationPdf({
    orientation,
    operationType:
      operationType === 'work-order'
        ? OperationTypeEnum.WORK_ORDER
        : OperationTypeEnum.APPLICATION,
    onError: () =>
      notification('PDF_EXPORT_ERROR', () => {}, {
        onRetry: () => {
          const last = lastPdfActionRef.current;
          if (last && operation) {
            createPDF(operation as any, last.language, last.toPrint);
          }
        }
      })
  });

  const loadOrientationPreference = async () => {
    const savedPreference = await findPreferenceByKey(
      PreferenceKey.OPERATION_PDF_ORIENTATION,
      { userId }
    );
    const isLandscape = savedPreference?.preferenceValue?.isLandscape;
    if (isLandscape !== undefined) {
      const orientationPreference = isLandscape ? 'landscape' : 'portrait';
      setOrientation(orientationPreference);
    }
  };

  const updateOrientationPreference = async (
    newOrientation: OrientationOptions
  ) => {
    await upsertPreference({
      preferenceKey: PreferenceKey.OPERATION_PDF_ORIENTATION,
      preferenceValue: { isLandscape: newOrientation === 'landscape' },
      userId
    });
  };

  const onOpenExportModal = async (chosenLanguage: ExportLanguageEnum) => {
    setLanguage(chosenLanguage);
    // Fetch saved orientation preference
    await loadOrientationPreference();
    setIsExportModalOpen(true);
  };

  const getOrientationLabel = (value: OrientationOptions) =>
    value === 'landscape' ? 'Landscape' : 'Portrait';

  const getLanguageLabel = () =>
    language === ExportLanguageEnum.ENGLISH ? 'English' : 'Spanish';

  const sendPrintPdfEvent = (
    buttonLocation: 'PDF Settings modal' | 'Confirmation toast',
    orientationOverride?: OrientationOptions
  ) => {
    sendPrintPdfAmplitudeEvent(
      getOrientationLabel(orientationOverride ?? orientation),
      buttonLocation,
      getLanguageLabel()
    );
  };

  const handleExport = async () => {
    setIsExportModalOpen(false);
    await updateOrientationPreference(orientation);
    if (operation) {
      lastPdfActionRef.current = { language, toPrint: false };
      sendDownloadPdfEvent(
        getOrientationLabel(orientation),
        getLanguageLabel()
      );
      await createPDF(operation as any, language, false);
    }
  };

  const handlePrint = async () => {
    setIsExportModalOpen(false);
    await updateOrientationPreference(orientation);

    if (operation) {
      lastPdfActionRef.current = { language, toPrint: true };
      sendPrintPdfEvent('PDF Settings modal');
      await createPDF(operation as any, language, true);
    }
  };

  // When products are loaded for the print application, enrich and print
  useEffect(() => {
    if (pendingPrintOperation && printProducts.length > 0) {
      const applicationWithTargetPests = {
        ...pendingPrintOperation,
        products: pendingPrintOperation.products?.map((product: Product) => {
          const printProduct: FullProduct | undefined = printProducts.find(
            _printProduct =>
              _printProduct?.productId !== undefined &&
              _printProduct.productId === product.productId
          );

          const productName =
            getProductName(product) ?? getProductName(printProduct);
          const manufacturerName =
            getManufacturerName(product) ?? getManufacturerName(printProduct);

          return {
            ...product,
            name: productName,
            productName,
            manufacturer: manufacturerName,
            manufacturerName,
            targetPests: getTargetPests(product, printProducts)
          };
        })
      };

      createPDF(applicationWithTargetPests, language, true);
      // Reset state
      setPendingPrintOperation(null);
      setPrintOperationId(null);
    }
  }, [printProducts, pendingPrintOperation, createPDF, language]);

  // Listen for print events from notification for both applications and work orders
  useEffect(() => {
    const eventName =
      operationType === AgOperationType.APPLICATION
        ? 'printApplication'
        : 'printWorkOrder';
    const dataKey =
      operationType === AgOperationType.APPLICATION
        ? 'application'
        : 'workOrder';

    const handlePrintEvent = async (event: CustomEvent) => {
      const operationFromEvent = event.detail[dataKey];
      if (operationFromEvent) {
        const savedPreference = await findPreferenceByKey(
          PreferenceKey.OPERATION_PDF_ORIENTATION,
          { userId }
        );
        const isLandscape = savedPreference?.preferenceValue?.isLandscape;
        const resolvedOrientation: OrientationOptions = isLandscape
          ? 'landscape'
          : 'portrait';
        setOrientation(resolvedOrientation);
        sendPrintPdfEvent('Confirmation toast', resolvedOrientation);

        // Trigger the hook to fetch products for this operation
        setPrintOperationId(operationFromEvent.id);
        setPendingPrintOperation(operationFromEvent);
      }
    };

    globalThis.addEventListener(eventName as any, handlePrintEvent as any);

    // Signal that the print listener is ready
    setPrintListenerReady(true);

    return () => {
      setPrintListenerReady(false);
      globalThis.removeEventListener(eventName as any, handlePrintEvent as any);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, operationType]);

  return {
    isExportModalOpen,
    setIsExportModalOpen,
    orientation,
    setOrientation,
    onOpenExportModal,
    handleExport,
    handlePrint,
    downloadingPDF
  };
};

export default usePrintSettingsModal;
